/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { alpha, type Theme } from '@mui/material/styles';
import { component, getThemeTokens } from '../../theme/tokens';
import type { AgentLauncherStatus } from './launcherStatus';

const { gradient } = component.light.agent;
const AGENT_GRADIENT = `linear-gradient(135deg, ${gradient.start}, ${gradient.mid} 60%, ${gradient.end})`;

const EDGE_PX = 18;

/**
 * Option 2b styling for the Agent chip, inline in the top bar or floating bottom-right.
 * While dragged it lifts above its neighbours and drops the backdrop blur, which would otherwise
 * be re-sampled every frame.
 */
export const getLauncherChipSx = (
  theme: Theme,
  {
    floating,
    isDragging,
    status,
    modifierHeld,
  }: {
    floating: boolean;
    isDragging: boolean;
    status: AgentLauncherStatus;
    modifierHeld: boolean;
  },
) => {
  const idle = status === 'idle';
  const tokens = getThemeTokens(theme);
  const surface = alpha(tokens.background.raised, 0.92);
  return {
    ...(floating && {
      bottom: EDGE_PX,
      right: EDGE_PX,
      zIndex: theme.zIndex.appBar - 1,
    }),
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    gap: '7px',
    height: 30,
    pl: 0,
    pr: modifierHeld ? '5px' : '12px',
    position: floating ? 'fixed' : 'relative',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: idle ? '0.6px' : '0.2px',
    whiteSpace: 'nowrap',
    color: idle ? 'text.secondary' : 'primary.main',
    backgroundColor: surface,
    // A masked ring keeps the translucent raised surface independent of the gradient.
    '&::before': {
      content: "''",
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '1.5px',
      background: AGENT_GRADIENT,
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    backdropFilter: isDragging ? 'none' : 'blur(14px)',
    boxShadow: `0 10px 24px ${tokens.shadow.contact}, 0 3px 14px -2px ${alpha(gradient.mid, 0.45)}`,
    transition: isDragging ? 'none' : 'padding-right 120ms, translate 120ms, box-shadow 120ms',
    ...(!isDragging && {
      '&:hover': {
        translate: '0 -1px',
        boxShadow: `0 10px 24px ${tokens.shadow.contact}, 0 3px 14px -2px ${alpha(gradient.mid, 0.6)}`,
      },
    }),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none', translate: 'none' },
    cursor: isDragging ? 'grabbing' : 'pointer',
    touchAction: 'none',
    userSelect: 'none',
    ...(isDragging && {
      zIndex: theme.zIndex.appBar + 1,
      willChange: 'transform',
    }),
    '&.Mui-focusVisible': { outline: '2px solid', outlineOffset: 2 },
  } as const;
};
